const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function fetchAPI(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export interface TutorMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface TutorConversation {
  id: number;
  title: string;
  courseId?: number;
  courseTitle?: string;
  lessonId?: number;
  lessonTitle?: string;
  messages: TutorMessage[];
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TutorResponse {
  conversationId: number;
  response: string;
  messages: TutorMessage[];
}

export interface QuickHelpResponse {
  response: string;
}

export type QuickHelpType = 'explain' | 'example' | 'quiz' | 'summary';

class TutorService {
  /**
   * Ask a question to the AI tutor
   */
  async askQuestion(
    question: string,
    options: {
      courseId?: number;
      lessonId?: number;
      conversationId?: number;
    } = {}
  ): Promise<TutorResponse> {
    return fetchAPI(`${API_BASE_URL}/api/tutor/ask`, {
      method: 'POST',
      body: JSON.stringify({
        question,
        courseId: options.courseId,
        lessonId: options.lessonId,
        conversationId: options.conversationId,
      }),
    });
  }

  /**
   * Get quick help for a lesson
   */
  async getQuickHelp(lessonId: number, helpType: QuickHelpType): Promise<string> {
    const data = await fetchAPI(`${API_BASE_URL}/api/tutor/quick-help`, {
      method: 'POST',
      body: JSON.stringify({
        lessonId,
        helpType,
      }),
    });
    return data.response;
  }

  /**
   * Get all conversations for the current user
   */
  async getConversations(courseId?: number): Promise<TutorConversation[]> {
    const url = courseId
      ? `${API_BASE_URL}/api/tutor/conversations?courseId=${courseId}`
      : `${API_BASE_URL}/api/tutor/conversations`;
    return fetchAPI(url);
  }

  /**
   * Get a single conversation
   */
  async getConversation(conversationId: number): Promise<TutorConversation> {
    return fetchAPI(`${API_BASE_URL}/api/tutor/conversation/${conversationId}`);
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: number): Promise<void> {
    await fetchAPI(`${API_BASE_URL}/api/tutor/conversation/${conversationId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get quick help options with labels
   */
  getQuickHelpOptions(): { type: QuickHelpType; label: string; icon: string }[] {
    return [
      { type: 'explain', label: 'Einfacher erklärt', icon: '💡' },
      { type: 'example', label: 'Beispiel zeigen', icon: '📝' },
      { type: 'quiz', label: 'Teste mich', icon: '❓' },
      { type: 'summary', label: 'Zusammenfassung', icon: '📋' },
    ];
  }
}

export const tutorService = new TutorService();
export default tutorService;
